import { useEffect, useRef, useState } from "react";

export type Geo = {
  lat: number;
  lon: number;
  heading: number | null;
  speedMps: number | null;
  accuracy: number;
  ts: number;
};

const STORAGE_KEY = "taze:lastGeo";

function readPersisted(): Geo | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as Geo;
  } catch {
    return null;
  }
}

function persist(geo: Geo) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(geo));
  } catch {}
}

export function useGeolocation(): {
  geo: Geo | null;
  error: string | null;
  permissionState: PermissionState | "unknown";
} {
  const [geo, setGeo] = useState<Geo | null>(() => readPersisted());
  const [error, setError] = useState<string | null>(null);
  const [permissionState, setPermissionState] = useState<
    PermissionState | "unknown"
  >("unknown");
  const lastWriteRef = useRef(0);

  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setError("Geolocation API not available");
      return;
    }

    if (navigator.permissions?.query) {
      navigator.permissions
        .query({ name: "geolocation" })
        .then((status) => {
          setPermissionState(status.state);
          status.onchange = () => setPermissionState(status.state);
        })
        .catch(() => undefined);
    }

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const next: Geo = {
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          heading: pos.coords.heading ?? null,
          speedMps: pos.coords.speed ?? null,
          accuracy: pos.coords.accuracy,
          ts: pos.timestamp,
        };
        setGeo(next);
        setError(null);

        const now = Date.now();
        if (now - lastWriteRef.current > 5_000) {
          lastWriteRef.current = now;
          persist(next);
        }
      },
      (err) => {
        setError(err.message);
      },
      {
        enableHighAccuracy: true,
        maximumAge: 2_000,
        timeout: 30_000,
      },
    );

    return () => navigator.geolocation.clearWatch(watchId);
  }, []);

  return { geo, error, permissionState };
}
